import { faTrash } from '@fortawesome/duotone-regular-svg-icons';
import { faHistory, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FormAsyncValidateOrFn,
  FormValidateOrFn,
  ReactFormExtendedApi,
  useForm,
} from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useReducer } from 'react';

import { StyledAuditHistoryButton } from '~/common/components/AuditHistoryButton';
import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import DatePicker from '~/common/components/DatePicker';
import FormLabel from '~/common/components/FormLabel';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import Spinner from '~/common/components/Spinner';
import { useAuditHistoryWindow } from '~/common/hooks/useAuditHistoryWindow';
import useUrl from '~/common/hooks/useUrl';
import Divider from '~/common/mui/Divider';
import TextField from '~/common/mui/Inputs/TextField';
import MUIDialog from '~/common/mui/MUIDialog/MUIDialog';
import Notice from '~/common/mui/MUINotice/Notice';
import { typedApiClient } from '~/common/util/api-client';
import { useModalState } from '~/modals/modalStore';
import { AuditHistoryPageEnum } from '~/pages/AuditHistory/constants';
import palette from '~/theme/mui/palette';

import { formatNewDateTrackingValues } from './utils';

function setDefaultIfEmpty(
  dataTrackItem?: {
    id?: number;
    date?: string | null;
    reason?: string | null;
  }[],
) {
  return Array.isArray(dataTrackItem) && dataTrackItem.length > 0
    ? dataTrackItem
    : [{ date: null, reason: null }];
}

type DateTrackingForm = ReactFormExtendedApi<
  InitialFormValues,
  FormValidateOrFn<InitialFormValues>,
  FormValidateOrFn<InitialFormValues>,
  FormAsyncValidateOrFn<InitialFormValues>,
  FormValidateOrFn<InitialFormValues>,
  FormAsyncValidateOrFn<InitialFormValues>,
  FormValidateOrFn<InitialFormValues>,
  FormAsyncValidateOrFn<InitialFormValues>,
  FormValidateOrFn<InitialFormValues>,
  FormAsyncValidateOrFn<InitialFormValues>,
  FormAsyncValidateOrFn<InitialFormValues>,
  unknown
>;

export type InitialFormValues = {
  obfuscatedLoanIdentifier?: {
    obfuscatedLenderDatabaseId?: number;
    obfuscatedLoanRecordId?: number;
  };
  estimatedClosingDate?: string;
  cannotCloseBeforeDateTracking?: { date?: string; reason?: string }[];
  mustCloseByDateTracking?: { date?: string; reason?: string }[];
};

type DateTrackingItem = {
  id?: number;
  date: string | null;
  reason: string | null;
};

type DateTrackingFormData = {
  mustCloseByDateTracking: DateTrackingItem[];
  cannotCloseBeforeDateTracking: DateTrackingItem[];
  estimatedClosingDate: string | null;
};

export default function DateTracking() {
  const { isOpen, onClose } = useModalState('dateTracking');
  const showSnackbar = useSnackbar();
  const { obfuscatedLoanIdentifier } = useUrl();
  const { handleChangePageId } = useAuditHistoryWindow('summary/date-tracking');
  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};

  const { data, isError, isLoading } = useQuery({
    queryKey: ['getDateTracking', obfuscatedLoanIdentifier],
    queryFn: async () => {
      const response = await typedApiClient.v1.underwritingDateTrackingDetail(
        obfuscatedLenderDatabaseId,
        obfuscatedLoanRecordId,
      );
      return response.data;
    },
    enabled:
      !!isOpen && !!obfuscatedLenderDatabaseId && !!obfuscatedLoanRecordId,
  });

  useEffect(() => {
    if (isError) {
      showSnackbar({
        severity: 'error',
        message: 'Could not fetch Date Tracking data.',
        duration: 5000,
      });
    }
  }, [isError, showSnackbar]);

  if (isLoading || !data) {
    return (
      <MUIDialog
        open={isOpen}
        onClose={onClose}
        title="Date Tracking"
        paperSx={{
          width: '37.5rem',
          maxHeight: 'min(54.5rem, calc(100dvh - 200px))',
          overflow: 'hidden',
        }}
        dialogPosition="left"
        anchorSelector='[data-modal-anchor="dateTracking"]'
        dividers={true}
        contentSx={{ backgroundColor: 'gray.100', overflowY: 'auto' }}
      >
        <Spinner backgroundColor="transparent" height="calc(100vh - 400px)" />
      </MUIDialog>
    );
  }

  return (
    <DateTrackingLoaded
      data={data}
      isOpen={isOpen}
      onClose={onClose}
      handleChangePageId={handleChangePageId}
    />
  );
}

type DateTrackingLoadedProps = {
  data: { initialFormValues?: InitialFormValues };
  isOpen: boolean;
  onClose: () => void;
  handleChangePageId: (pageId: number | string) => void;
};

function DateTrackingLoaded({
  data,
  isOpen,
  onClose,
  handleChangePageId,
}: Readonly<DateTrackingLoadedProps>) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();
  const { obfuscatedLoanIdentifier } = useUrl();

  const form = useForm({
    defaultValues: {
      mustCloseByDateTracking: setDefaultIfEmpty(
        data.initialFormValues?.mustCloseByDateTracking,
      ) as InitialFormValues['mustCloseByDateTracking'],
      cannotCloseBeforeDateTracking: setDefaultIfEmpty(
        data.initialFormValues?.cannotCloseBeforeDateTracking,
      ) as InitialFormValues['cannotCloseBeforeDateTracking'],
      estimatedClosingDate:
        data.initialFormValues?.estimatedClosingDate ?? null,
    } as InitialFormValues,
    onSubmit: () => {},
  });

  // form.state is a snapshot; reads here are re-evaluated on every render
  const mustCloseByDates = form.state.values.mustCloseByDateTracking;
  const cannotCloseByDates = form.state.values.cannotCloseBeforeDateTracking;
  const mustCloseByFields = (mustCloseByDates ?? []) as DateTrackingItem[];
  const cannotCloseByFields = (cannotCloseByDates ?? []) as DateTrackingItem[];
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0);

  const appendMustCloseBy = (val: DateTrackingItem) => {
    form.pushFieldValue('mustCloseByDateTracking' as never, val as never);
    forceUpdate();
  };
  const removeMustCloseBy = (idx: number) => {
    form.removeFieldValue('mustCloseByDateTracking' as never, idx);
    forceUpdate();
  };
  const appendCannotCloseBy = (val: DateTrackingItem) => {
    form.pushFieldValue('cannotCloseBeforeDateTracking' as never, val as never);
    forceUpdate();
  };
  const removeCannotCloseBy = (idx: number) => {
    form.removeFieldValue('cannotCloseBeforeDateTracking' as never, idx);
    forceUpdate();
  };

  const THRESHOLD = 1;

  const mustCloseByDatesLengthGreaterThan1 =
    Array.isArray(mustCloseByDates) && mustCloseByDates.length > THRESHOLD;
  const cannotCloseByDatesLengthGreaterThan1 =
    Array.isArray(cannotCloseByDates) && cannotCloseByDates.length > THRESHOLD;

  const { mutate: saveDateTrackingInformation, isLoading: isSubmitting } =
    useMutation({
      mutationKey: ['saveDateTrackingInformation'],
      mutationFn: async (dateTrackingData: InitialFormValues) => {
        await typedApiClient.v1.underwritingDateTracking(dateTrackingData);
      },
      onSuccess: () => {
        queryClient.invalidateQueries([
          'getDateTracking',
          obfuscatedLoanIdentifier,
        ]);
        // HOI estimated closing date condition needs to be updated after save
        queryClient.invalidateQueries(['GetInsuranceDataAsync']);
        showSnackbar({
          severity: 'success',
          message: 'Date Tracking data saved successfully.',
          duration: 5000,
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          severity: 'error',
          message:
            'There was an error saving. Try again, and submit a ticket via YouSupport if the issue persists.',
          duration: 5000,
        });
      },
    });

  const handleSubmit = useCallback(
    (dateTrackingFormData: DateTrackingFormData) => {
      const dateTrackingValues = formatNewDateTrackingValues(
        data.initialFormValues,
        dateTrackingFormData,
      );
      saveDateTrackingInformation(dateTrackingValues);
    },
    [data, saveDateTrackingInformation],
  );

  const onSubmit = useCallback(() => {
    handleSubmit(form.state.values as DateTrackingFormData);
  }, [form.state.values, handleSubmit]);

  return (
    <MUIDialog
      open={isOpen}
      onClose={onClose}
      title="Date Tracking"
      paperSx={{
        width: '37.5rem',
        maxHeight: 'min(54.5rem, calc(100dvh - 200px))',
        overflow: 'hidden',
      }}
      dialogPosition="left"
      anchorSelector='[data-modal-anchor="dateTracking"]'
      dividers={true}
      contentSx={{ backgroundColor: 'gray.100', overflowY: 'auto' }}
      actions={
        <DateTrackingFooter
          form={form}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          isLoading={false}
          handleChangePageId={handleChangePageId}
        />
      }
    >
      <DateTrackingContent
        form={form}
        mustCloseByFields={mustCloseByFields}
        cannotCloseByFields={cannotCloseByFields}
        appendMustCloseBy={appendMustCloseBy}
        removeMustCloseBy={removeMustCloseBy}
        appendCannotCloseBy={appendCannotCloseBy}
        removeCannotCloseBy={removeCannotCloseBy}
        mustCloseByDatesLengthGreaterThan1={mustCloseByDatesLengthGreaterThan1}
        cannotCloseByDatesLengthGreaterThan1={
          cannotCloseByDatesLengthGreaterThan1
        }
      />
    </MUIDialog>
  );
}

type DateTrackingContentProps = {
  form: DateTrackingForm;
  mustCloseByFields: DateTrackingItem[];
  cannotCloseByFields: DateTrackingItem[];
  appendMustCloseBy: (value: DateTrackingItem) => void;
  removeMustCloseBy: (index: number) => void;
  appendCannotCloseBy: (value: DateTrackingItem) => void;
  removeCannotCloseBy: (index: number) => void;
  mustCloseByDatesLengthGreaterThan1: boolean;
  cannotCloseByDatesLengthGreaterThan1: boolean;
};

function DateTrackingContent({
  form,
  mustCloseByFields,
  cannotCloseByFields,
  appendMustCloseBy,
  removeMustCloseBy,
  appendCannotCloseBy,
  removeCannotCloseBy,
  mustCloseByDatesLengthGreaterThan1,
  cannotCloseByDatesLengthGreaterThan1,
}: Readonly<DateTrackingContentProps>) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {mustCloseByDatesLengthGreaterThan1 && (
        <Notice
          severity="info"
          message="Earliest Must Close By Date will be saved."
          sx={{ mb: '0.25rem' }}
        />
      )}
      {mustCloseByFields.map((item, index) => (
        <Box key={item.id}>
          <DateEntryCard
            form={form}
            fieldPath={`mustCloseByDateTracking.${index}`}
            dateLabel="Must Close By"
            showDelete={mustCloseByDatesLengthGreaterThan1}
            onDelete={() => removeMustCloseBy(index)}
            deleteAriaLabel={`Delete Must Close By Date ${index + 1}`}
          />
          {index < mustCloseByFields.length - 1 && (
            <Divider sx={{ my: '0.25rem' }} />
          )}
        </Box>
      ))}
      {mustCloseByFields.length < 10 && (
        <Button
          isPrimary={false}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          alignSelf="flex-start"
          bg="white"
          onClick={() => appendMustCloseBy({ date: null, reason: null })}
        >
          Add Must Close By Date
        </Button>
      )}
      <Divider sx={{ my: '0.25rem' }} />
      {cannotCloseByDatesLengthGreaterThan1 && (
        <Notice
          severity="info"
          message="Latest Cannot Close Before Date will be saved."
          sx={{ mb: '0.25rem' }}
        />
      )}
      {cannotCloseByFields.map((item, index) => (
        <Box key={item.id}>
          <DateEntryCard
            form={form}
            fieldPath={`cannotCloseBeforeDateTracking.${index}`}
            dateLabel="Cannot Close Before"
            showDelete={cannotCloseByDatesLengthGreaterThan1}
            onDelete={() => removeCannotCloseBy(index)}
            deleteAriaLabel={`Delete Cannot Close Before Date ${index + 1}`}
          />
          {index < cannotCloseByFields.length - 1 && (
            <Divider sx={{ my: '0.25rem' }} />
          )}
        </Box>
      ))}
      {cannotCloseByFields.length < 10 && (
        <Button
          isPrimary={false}
          leftIcon={<FontAwesomeIcon icon={faPlus} />}
          alignSelf="flex-start"
          bg="white"
          onClick={() => appendCannotCloseBy({ date: null, reason: null })}
        >
          Add Cannot Close Before Date
        </Button>
      )}
    </Box>
  );
}

type DateEntryCardProps = {
  form: DateTrackingForm;
  fieldPath: string;
  dateLabel: string;
  showDelete: boolean;
  onDelete: () => void;
  deleteAriaLabel: string;
};

function DateEntryCard({
  form,
  fieldPath,
  dateLabel,
  showDelete,
  onDelete,
  deleteAriaLabel,
}: Readonly<DateEntryCardProps>) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <form.Field name={`${fieldPath}.date`}>
        {(field) => (
          <Box
            sx={{
              '& .date-picker .MuiInputBase-root': {
                backgroundColor: 'white !important',
              },
              '& .date-picker .MuiInputAdornment-root:before': {
                backgroundColor: 'white !important',
              },
            }}
          >
            <DatePicker
              name={`${fieldPath}.date`}
              label={dateLabel}
              placeholder="Select a date"
              value={
                typeof field.state.value === 'string' ? field.state.value : ''
              }
              onChange={field.handleChange}
              onBlur={field.handleBlur}
              width="12rem"
            />
          </Box>
        )}
      </form.Field>
      <Box sx={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            flex: 1,
          }}
        >
          <FormLabel label="Reason" sx={{ marginBottom: '-0.06rem' }} />
          <form.Field name={`${fieldPath}.reason`}>
            {(field) => (
              <TextField
                value={
                  typeof field.state.value === 'string' ? field.state.value : ''
                }
                onChange={(e) =>
                  (field.handleChange as (v: string) => void)(e.target.value)
                }
                onBlur={field.handleBlur}
                size="small"
                fullWidth
                multiline
                placeholder="Enter reason"
                slotProps={{
                  input: {
                    inputComponent: 'textarea' as any,
                    sx: {
                      alignItems: 'flex-start',
                      '& textarea': {
                        resize: 'vertical',
                        overflow: 'auto',
                        minHeight: '40px',
                        height: '40px',
                      },
                    },
                  },
                }}
              />
            )}
          </form.Field>
        </Box>
        {showDelete && (
          <Button
            isPrimary={true}
            aria-label={deleteAriaLabel}
            data-testid="delete-icon-button"
            onClick={onDelete}
            backgroundColor="transparent"
            _hover={{ backgroundColor: 'transparent' }}
            _active={{ backgroundColor: 'transparent' }}
            _focus={{ backgroundColor: 'transparent' }}
            width="3rem"
            flexShrink={0}
          >
            <FontAwesomeIcon icon={faTrash} color={palette.blue[600]} />
          </Button>
        )}
      </Box>
    </Box>
  );
}

type DateTrackingFooterProps = {
  form: DateTrackingForm;
  isSubmitting: boolean;
  onSubmit: () => void;
  isLoading: boolean;
  handleChangePageId: (pageId: number | string) => void;
};

function DateTrackingFooter({
  form,
  isSubmitting,
  onSubmit,
  isLoading,
  handleChangePageId,
}: Readonly<DateTrackingFooterProps>) {
  return (
    <Box
      sx={{
        padding: '1rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
      }}
    >
      <Box sx={{ maxWidth: '14rem' }}>
        <form.Field name="estimatedClosingDate">
          {(field) => (
            <Box
              sx={{
                '& .date-picker .MuiInputBase-root': {
                  backgroundColor: 'white !important',
                },
                '& .date-picker .MuiInputAdornment-root:before': {
                  backgroundColor: 'white !important',
                },
              }}
            >
              <DatePicker
                name="estimatedClosingDate"
                label="Estimated Closing Date"
                placeholder="Select a date"
                value={
                  typeof field.state.value === 'string' ? field.state.value : ''
                }
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                data-testid="estimatedClosingDate"
              />
            </Box>
          )}
        </form.Field>
      </Box>

      <form.Subscribe
        selector={(state) => {
          const estimatedDate = state.values.estimatedClosingDate;
          const mustCloseByDateTracking = state.values.mustCloseByDateTracking;
          const cannotCloseBeforeDateTracking =
            state.values.cannotCloseBeforeDateTracking;

          const mustCloseByDateLessThanEstimatedClosingDate =
            estimatedDate && Array.isArray(mustCloseByDateTracking)
              ? mustCloseByDateTracking.some(
                  (d) => d.date && new Date(d.date) < new Date(estimatedDate),
                )
              : false;

          const cannotCloseByDateGreaterThanEstimatedClosingDate =
            estimatedDate && Array.isArray(cannotCloseBeforeDateTracking)
              ? cannotCloseBeforeDateTracking.some(
                  (d) => d.date && new Date(d.date) > new Date(estimatedDate),
                )
              : false;

          const cannotCloseByDateGreaterThanMostCloseByDate =
            Array.isArray(mustCloseByDateTracking) &&
            Array.isArray(cannotCloseBeforeDateTracking)
              ? cannotCloseBeforeDateTracking.some(
                  (cannotCloseBefore) =>
                    !!cannotCloseBefore.date &&
                    mustCloseByDateTracking.some(
                      (mustCloseBy) =>
                        !!mustCloseBy.date &&
                        new Date(cannotCloseBefore.date) >
                          new Date(mustCloseBy.date),
                    ),
                )
              : false;

          return {
            mustCloseByDateLessThanEstimatedClosingDate,
            cannotCloseByDateGreaterThanEstimatedClosingDate,
            cannotCloseByDateGreaterThanMostCloseByDate,
          };
        }}
      >
        {({
          mustCloseByDateLessThanEstimatedClosingDate,
          cannotCloseByDateGreaterThanEstimatedClosingDate,
          cannotCloseByDateGreaterThanMostCloseByDate,
        }) => (
          <>
            {mustCloseByDateLessThanEstimatedClosingDate && (
              <Notice
                severity="extractionFailed"
                message="Estimated Closing Date is after Must Close By Date. Discussion to be had with broker to determine next steps."
              />
            )}
            {cannotCloseByDateGreaterThanEstimatedClosingDate && (
              <Notice
                severity="extractionFailed"
                message="Estimated Closing Date is before Cannot Close Before Date. Discussion to be had with broker to determine next steps."
              />
            )}
            {cannotCloseByDateGreaterThanMostCloseByDate && (
              <Notice
                severity="error"
                message="The current dates are not acceptable as the Must Close By Date is before the Cannot Close Before Date. Update at least one of these dates to proceed."
              />
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <StyledAuditHistoryButton
                rightIcon={<FontAwesomeIcon icon={faHistory} />}
                data-testid="audit-history--button"
                aria-label="Date Tracking Audit History"
                isPrimary={false}
                isDisabled={isLoading}
                onClick={() =>
                  handleChangePageId(AuditHistoryPageEnum.DATE_TRACKING)
                }
              />
              <Button
                isLoading={isSubmitting}
                isDisabled={cannotCloseByDateGreaterThanMostCloseByDate}
                isPrimary={true}
                onClick={onSubmit}
              >
                Save Dates
              </Button>
            </Box>
          </>
        )}
      </form.Subscribe>
    </Box>
  );
}
